import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import toast, { Toaster } from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { addToCart } from "../../../ProductDetail/service/productcart.service";
import { getCustomers } from "../../../Cart/service/Customers.service";
import { getCartCount } from "../../../../Component/header/service/CartCount.service";

function CartAdd({ product }) {
  const [isLoading] = useState(false);
  const [quantity, setQuantity] = useState("");
  const [isCustomInput, setIsCustomInput] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  // =====================================================
  // CUSTOMER ID
  // =====================================================
  const getCustomerId = (customer) => {
    if (!customer) return null;
    return (
      customer.customerId ||
      customer.customer_id ||
      customer.id ||
      null
    );
  };

  // =====================================================
  // CUSTOMER NAME
  // =====================================================
  const getCustomerName = (customer) => {
    return (
      customer?.accountName ||
      customer?.customerName ||
      customer?.customer_name ||
      customer?.name ||
      "Customer"
    );
  };

  // =====================================================
  // QUANTITY SELECT
  // =====================================================
  const handleSelectChange = async (e) => {
    const value = e.target.value;
    if (value === "10+") {
      setQuantity("10");
      setIsCustomInput(true);
      setIsUpdateMode(true);
      return;
    }

    setIsCustomInput(false);
    setQuantity(value);

    // 0 selected => button disabled
    if (!value || Number(value) <= 0) {
      setIsUpdateMode(false);
      return;
    }

    // 1-9 => normal auto add
    setIsUpdateMode(false);
    await handleAddCart(Number(value));
  };

  // =====================================================
  // CUSTOM QUANTITY INPUT
  // =====================================================
const handleCustomInputChange = (e) => {
  const value = e.target.value;

  // ONLY NUMBERS
  if (!/^\d*$/.test(value)) {
    return;
  }

  const stock = Number(
    product?.stock ??
      product?.avail_stock ??
      0
  );

  // Empty allowed while typing
  if (value === "") {
    setQuantity("");
    setIsUpdateMode(true);
    return;
  }

  const qty = Number(value);

  // Value ko change mat karo.
  // Stock se zyada hai to bhi user ko typed value dikhni chahiye.
  setQuantity(value);

  // 10+ custom input => UPDATE mode
  setIsUpdateMode(true);

  // Stock se zyada hai to message
  if (qty > stock) {
    toast.error(`Only ${stock} quantity available`);
  }
};

  // =====================================================
  // LOAD CUSTOMERS
  // =====================================================
  const loadCustomers = async () => {
    try {
      const response = await getCustomers({});

      const customerList = Array.isArray(response)
        ? response
        : response?.data || [];

      setCustomers(customerList);

      return customerList;
    } catch (error) {
      console.error("CUSTOMER API ERROR =>", error);

      toast.error(
        error?.response?.data?.message ||
          "Unable to load customers"
      );

      return [];
    }
  };

  // =====================================================
  // REFRESH CART COUNT
  // =====================================================
  const refreshCartCount = async (
    userId,
    customerId
  ) => {
    try {
      const response = await getCartCount(
        "fresh",
        Number(userId),
        Number(customerId)
      );

      const data =
        response?.data?.data ||
        response?.data ||
        response ||
        {};

      const count = Number(
        data?.count ??
          data?.cart_count ??
          data?.total_items ??
          0
      );

      const responseCustomerId = Number(
        data?.customer_id ??
          data?.customerId ??
          customerId
      );

      localStorage.setItem(
        "cart_count",
        String(count)
      );

      localStorage.setItem(
        "customer_id",
        String(responseCustomerId)
      );

      window.dispatchEvent(
        new CustomEvent("cartCountUpdated", {
          detail: {
            count,
            customer_id: responseCustomerId,
            user_id: Number(userId),
          },
        })
      );

      return {
        count,
        customerId: responseCustomerId,
      };
    } catch (error) {
      console.error(
        "CART COUNT ERROR =>",
        error
      );

      return null;
    }
  };

  // =====================================================
  // REMOVE FROM WISHLIST EVENT
  // =====================================================
  const removeFromWishlistAfterCart = (
    userId,
    customerId
  ) => {
    const productListingId = Number(
      product?.product_listing_id
    );

    if (!productListingId) {
      return;
    }

    const wishlistId =
      product?.wishlist_id ||
      product?.wishlistId ||
      product?.wishlistID ||
      product?.wishlist?.id ||
      null;

    window.dispatchEvent(
      new CustomEvent("productAddedToCart", {
        detail: {
          product_listing_id: productListingId,
          wishlist_id: wishlistId
            ? Number(wishlistId)
            : null,
          user_id: Number(userId),
          customer_id: Number(customerId),
        },
      })
    );
  };

  // =====================================================
  // ADD / UPDATE PRODUCT TO CART
  // =====================================================
  const addProductToCart = async (
    customerId,
    userId,
    customer = null,
    selectedQty = null
  ) => {
    if (!customerId) {
      toast.error("Customer ID not found");
      return false;
    }

    const productListingId = Number(
      product?.product_listing_id
    );

    const qty = Number(
      selectedQty !== null
        ? selectedQty
        : quantity
    );

    const availableStock = Number(
      product?.stock ??
        product?.avail_stock ??
        0
    );

    if (!productListingId) {
      toast.error(
        "Product listing ID not found"
      );
      return false;
    }

    // Quantity select/type nahi ki hai
    if (!qty || qty <= 0) {
      toast.error("Select Quantity");
      return false;
    }

    if (availableStock <= 0) {
      toast.error(
        "Product is out of stock"
      );
      return false;
    }

    // Stock se zyada quantity allowed nahi
    if (qty > availableStock) {
      toast.error(
        `Only ${availableStock} quantity available`
      );
      return false;
    }

    const payload = {
      cart_type: "fresh",
      user_id: Number(userId),
      customer_id: Number(customerId),
      product_listing_id: productListingId,
      qty: qty,
    };

    try {
      setLoading(true);

      const response =
        await addToCart(payload);

      if (
        response?.success === false ||
        response?.data?.success === false
      ) {
        toast.error(
          response?.message ||
            response?.data?.message ||
            "Update Cart Failed"
        );

        return false;
      }

      localStorage.setItem(
        "customer_id",
        String(customerId)
      );

      localStorage.setItem(
        "user_id",
        String(userId)
      );

      if (customer) {
        localStorage.setItem(
          "selectedCustomer",
          JSON.stringify(customer)
        );
      }

      await refreshCartCount(
        Number(userId),
        Number(customerId)
      );

      removeFromWishlistAfterCart(
        Number(userId),
        Number(customerId)
      );

      toast.success(
        isUpdateMode
          ? response?.message ||
              response?.data?.message ||
              "Quantity Updated Successfully"
          : response?.message ||
              response?.data?.message ||
              "Added Successfully"
      );

      setShowModal(false);
      setSearch("");
      setActiveIndex(-1);

      return true;
    } catch (error) {
      console.error(
        "ADD / UPDATE CART ERROR =>",
        error
      );

      toast.error(
        error?.response?.data?.message ||
          "Update Cart Failed"
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // CART / UPDATE BUTTON
  // =====================================================
  const handleAddCart = async (
    selectedQuantity = null
  ) => {
    if (!product?.product_listing_id) {
      toast.error("Product not found");
      return;
    }

    const qty = Number(
      selectedQuantity !== null
        ? selectedQuantity
        : quantity
    );

    // Quantity select/type nahi ki
    if (!qty || qty <= 0) {
      toast.error("Select Quantity");
      return;
    }

    const stock = Number(
      product?.stock ??
        product?.avail_stock ??
        0
    );

    if (stock <= 0) {
      toast.error(
        "Product is out of stock"
      );
      return;
    }

    if (qty > stock) {
      toast.error(
        `Only ${stock} quantity available`
      );
      return;
    }

    const userId =
      Number(
        localStorage.getItem("user_id")
      ) || 1;

    try {
      setLoading(true);

      // ---------------------------------------------
      // CUSTOMER ALREADY SELECTED
      // ---------------------------------------------
      const savedCustomerId = Number(
        localStorage.getItem("customer_id")
      );

      if (savedCustomerId > 0) {
        await addProductToCart(
          savedCustomerId,
          userId,
          null,
          qty
        );

        return;
      }

      // ---------------------------------------------
      // CHECK EXISTING CART
      // ---------------------------------------------
      const response =
        await getCartCount(
          "fresh",
          userId,
          null
        );

      const data =
        response?.data?.data ||
        response?.data ||
        response ||
        {};

      const cartCount = Number(
        data?.count ??
          data?.cart_count ??
          data?.total_items ??
          0
      );

      const cartCustomerId = Number(
        data?.customer_id ??
          data?.customerId ??
          0
      );

      // ---------------------------------------------
      // CART HAS CUSTOMER
      // ---------------------------------------------
      if (
        cartCount > 0 &&
        cartCustomerId > 0
      ) {
        await addProductToCart(
          cartCustomerId,
          userId,
          null,
          qty
        );

        return;
      }

      // ---------------------------------------------
      // NO CUSTOMER
      // ---------------------------------------------
      await loadCustomers();

      setSearch("");
      setActiveIndex(-1);
      setShowModal(true);
    } catch (error) {
      console.error(
        "AUTO ADD CART ERROR =>",
        error
      );

      toast.error(
        error?.response?.data?.message ||
          "Unable to add product"
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // CUSTOMER SELECT
  // =====================================================
  const handleCustomerSelect = async (
    customer
  ) => {
    if (!customer) return;

    const customerId =
      getCustomerId(customer);

    if (!customerId) {
      toast.error(
        "Customer ID not found"
      );
      return;
    }

    const userId =
      Number(
        localStorage.getItem("user_id")
      ) || 1;

    const qty = Number(quantity);

    if (!qty || qty <= 0) {
      toast.error("Select Quantity");
      return;
    }

    await addProductToCart(
      Number(customerId),
      Number(userId),
      customer,
      qty
    );
  };

  // =====================================================
  // FILTER CUSTOMERS
  // =====================================================
  const filteredCustomers =
    customers.filter((customer) => {
      const name =
        getCustomerName(customer);

      return name
        .toLowerCase()
        .includes(
          search.toLowerCase()
        );
    });

  // =====================================================
  // HIGHLIGHT
  // =====================================================
  const highlightText = (
    text = "",
    searchText = ""
  ) => {
    if (
      !searchText ||
      searchText.length < 2
    ) {
      return text;
    }

    const escapedSearch =
      searchText.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );

    const regex = new RegExp(
      `(${escapedSearch})`,
      "gi"
    );

    return text
      .split(regex)
      .map((part, index) =>
        part.toLowerCase() ===
        searchText.toLowerCase() ? (
          <span
            key={index}
            style={{
              fontWeight: "700",
              color: "#000",
            }}
          >
            {part}
          </span>
        ) : (
          part
        )
      );
  };

  // =====================================================
  // STOCK
  // =====================================================
  const stock = Number(
    product?.stock ??
      product?.avail_stock ??
      0
  );

  // Quantity selected/type ki hui hai?
const hasQuantity =
  quantity !== "" &&
  Number(quantity) > 0;

const isQuantityOverStock =
  hasQuantity &&
  Number(quantity) > stock;

  return (
    <>
      <Toaster position="top-right" />

      <div className="table-responsive table-cont table-box-content">
        <table className="table table-sm table-striped mb-0">
          <thead className="text-center">
            <tr>
              <th>Size</th>
              <th>Shape</th>
               <th>MRP (₹)</th>
              <th>Stock</th>
              <th>Ord. Qty</th>
              <th>Cart</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              {/* SIZE */}
              <td>
                {isLoading ? (
                  <Skeleton width={60} />
                ) : (
                  product?.size || "-"
                )}
              </td>

              {/* SHAPE */}
              <td>
                {isLoading ? (
                  <Skeleton width={80} />
                ) : (
                  product?.shape || "-"
                )}
              </td>

               {/* MRP */}
              <td>
                {isLoading ? (
                  <Skeleton width={80} />
                ) : (
                  product?.mrp ||
                  product?.price ||
                  "0.00"
                )}
              </td>

              {/* STOCK */}
              <td className="text-center">
                {isLoading ? (
                  <Skeleton width={40} />
                ) : (
                  stock
                )}
              </td>

              {/* QUANTITY */}
              <td style={{ width: 90 }}>
                {!isCustomInput ? (
                  <select
                    className="form-control form-control-sm text-center"
                    value={quantity}
                    onChange={
                      handleSelectChange
                    }
                    disabled={
                      loading ||
                      stock <= 0
                    }
                  >
                    <option value="">
                      0
                    </option>

                    {/* 1 TO 9 */}
                    {Array.from(
                      {
                        length: Math.min(
                          9,
                          stock
                        ),
                      },
                      (_, i) => i + 1
                    ).map((qty) => (
                      <option
                        key={qty}
                        value={qty}
                      >
                        {qty}
                      </option>
                    ))}

                    {/* 10+ */}
                    {stock >= 10 && (
                      <option value="10+">
                        10+
                      </option>
                    )}
                  </select>
                ) : (
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control form-control-sm text-center"
                    value={quantity}
                    onChange={
                      handleCustomInputChange
                    }
                    disabled={
                      loading ||
                      stock <= 0
                    }
                    autoFocus
                    onFocus={(e) =>
                      e.target.select()
                    }
                  />
                )}
              </td>

            

              {/* CART BUTTON */}
              <td className="text-center">
                <button
                  type="button"
                  className={`btn btn-sm addtocart ${
                    isUpdateMode
                      ? "btn-success"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() =>
                    handleAddCart()
                  }
                  disabled={
                    loading ||
                    !hasQuantity ||
                    stock <= 0 ||
                    isQuantityOverStock
                  }
                >
                  {loading ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : isUpdateMode ? (
                    <i className="bi bi-arrow-repeat me-1"></i>
                  ) : (
                    <i className="bi bi-cart-plus"></i>
                  )}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* CUSTOMER MODAL */}
      <Modal
        show={showModal}
        onHide={() => {
          if (!loading) {
            setShowModal(false);
            setSearch("");
            setActiveIndex(-1);
          }
        }}
        className="model001"
        style={{
          background: "#0000008a",
        }}
      >
        <Modal.Body>
          <div className="position-relative serchlistdata pt-2">
            <input
              type="search"
              className="form-control"
              placeholder="Search Customer..."
              autoFocus
              value={search}
              disabled={loading}
              style={{
                borderRadius: "5px",
                fontSize: "13px",
                height: "30px",
                marginTop: "0",
                width: "75%",
              }}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(-1);
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();

                  setActiveIndex(
                    (prev) =>
                      prev <
                      filteredCustomers.length - 1
                        ? prev + 1
                        : prev
                  );
                }

                if (e.key === "ArrowUp") {
                  e.preventDefault();

                  setActiveIndex(
                    (prev) =>
                      prev > 0
                        ? prev - 1
                        : 0
                  );
                }

                if (
                  e.key === "Enter" &&
                  activeIndex >= 0 &&
                  filteredCustomers[
                    activeIndex
                  ]
                ) {
                  e.preventDefault();

                  handleCustomerSelect(
                    filteredCustomers[
                      activeIndex
                    ]
                  );
                }
              }}
            />

            {search && (
              <div
                className="position-absolute"
                style={{
                  top: "calc(100% - 1px)",
                  left: 0,
                  maxHeight: "250px",
                  overflowY: "auto",
                  background: "#fff",
                  border: "1px solid #ddd",
                  borderTop: "none",
                  borderRadius:
                    "0 0 5px 5px",
                  boxShadow:
                    "rgba(0,0,0,.15) 0 15px 12px",
                  zIndex: 9999,
                  width: "75%",
                  fontSize: "13px",
                }}
              >
                {filteredCustomers.length >
                0 ? (
                  filteredCustomers.map(
                    (
                      customer,
                      index
                    ) => {
                      const customerId =
                        getCustomerId(
                          customer
                        );

                      return (
                        <div
                          key={
                            customerId
                              ? `customer-${customerId}-${index}`
                              : `customer-${index}`
                          }
                          onClick={() =>
                            handleCustomerSelect(
                              customer
                            )
                          }
                          onMouseEnter={() =>
                            setActiveIndex(
                              index
                            )
                          }
                          style={{
                            padding:
                              "7px 12px",
                            cursor:
                              "pointer",
                            background:
                              activeIndex ===
                              index
                                ? "#ededed"
                                : "#fff",
                            borderBottom:
                              "1px solid #ddd",
                          }}
                        >
                          {highlightText(
                            getCustomerName(
                              customer
                            ),
                            search
                          )}
                        </div>
                      );
                    }
                  )
                ) : (
                  <div
                    style={{
                      padding: 10,
                      textAlign:
                        "center",
                    }}
                  >
                    No customer found
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
export default CartAdd;